import { useState, useEffect } from "react";
import { collection, addDoc, getDocs, query, where, arrayUnion, updateDoc, doc, serverTimestamp, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import { nanoid } from "nanoid";

export default function Leagues() {
  const { currentUser } = useAuth();
  const [leagues, setLeagues] = useState([]);
  const [globalLeague, setGlobalLeague] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newLeagueName, setNewLeagueName] = useState("");
  const [joinCode, setJoinCode] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [globalMemberCount, setGlobalMemberCount] = useState(0);
  const GLOBAL_LEAGUE_PUBLIC = false; // 👈 change to true to show to everyone
const MAX_LEAGUES_PER_USER = 5;
  useEffect(() => {
    fetchLeagues();
    fetchGlobalLeague();
  }, []);

  async function fetchGlobalLeague() {
    const snap = await getDoc(doc(db, "leagues", "global_predictorz"));
    if (snap.exists()) setGlobalLeague({ id: snap.id, ...snap.data() });
    const usersSnap = await getDocs(collection(db, "users"));
    setGlobalMemberCount(usersSnap.size);
  }

  async function fetchLeagues() {
    setLoading(true);
    const q = query(collection(db, "leagues"), where("members", "array-contains", currentUser.uid));
    const snapshot = await getDocs(q);
    const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    setLeagues(data);
    setLoading(false);
  }

  async function handleCreateLeague(e) {
    e.preventDefault();
    if (!newLeagueName.trim()) return;
    const userLeagues = leagues.filter((l) => l.createdBy === currentUser.uid);
  if (userLeagues.length >= MAX_LEAGUES_PER_USER) {
    setError("You've reached the maximum number of leagues you can create.");
    return;
  }
    setCreating(true);
    setError("");
    try {
      const inviteCode = nanoid(6).toUpperCase();
      await addDoc(collection(db, "leagues"), {
        name: newLeagueName.trim(),
        createdBy: currentUser.uid,
        inviteCode,
        members: [currentUser.uid],
        isPublic: false,
        createdAt: serverTimestamp(),
      });
      setNewLeagueName("");
      setSuccess("League created!");
      fetchLeagues();
    } catch (err) {
      setError("Failed to create league.");
    }
    setCreating(false);
  }

  async function handleJoinLeague(e) {
    e.preventDefault();
    setError("");
    setSuccess("");
    const code = joinCode.trim().toUpperCase();
    try {
      const q = query(collection(db, "leagues"), where("inviteCode", "==", code));
      const snapshot = await getDocs(q);
      if (snapshot.empty) return setError("No league found with that code.");
      const leagueDoc = snapshot.docs[0];
      const data = leagueDoc.data();
      if (data.members?.includes(currentUser.uid)) {
        return setError("You're already in this league.");
      }
      if (data.pendingMembers?.includes(currentUser.uid)) {
        return setError("You've already requested to join this league. Waiting for approval.");
      }
      await updateDoc(doc(db, "leagues", leagueDoc.id), {
        pendingMembers: arrayUnion(currentUser.uid),
      });
      setJoinCode("");
      setSuccess(`Join request sent to "${data.name}"! The creator will need to approve it.`);
    } catch (err) {
      setError("Something went wrong. Please try again.");
    }
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#F0F1EC" }}>
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 py-10">

        <div className="mb-10 fade-up">
          <p className="font-condensed text-gray-500 tracking-widest uppercase text-sm mb-2">Competition</p>
          <h2 className="font-display text-7xl tracking-wider" style={{ color: "#0A0B0F" }}>MY LEAGUES</h2>
        </div>

        {error && (
          <div className="mb-6 px-4 py-3 rounded-xl font-condensed tracking-wide text-red-600"
            style={{ backgroundColor: "#FFF0F0", border: "1px solid #FFCCCC" }}>
            {error}
          </div>
        )}
        {success && (
          <div className="mb-6 px-4 py-3 rounded-xl font-condensed tracking-wide"
            style={{ backgroundColor: "#EDFFD0", border: "1px solid #C8FF00", color: "#3A6600" }}>
            ✓ {success}
          </div>
        )}

        {/* Global Public League */}
        {globalLeague && (GLOBAL_LEAGUE_PUBLIC || currentUser.email === "jonnybroughton@me.com") && (
          <div className="mb-10 fade-up">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: "#3B82F6" }} />
              <h3 className="font-condensed text-gray-500 tracking-widest uppercase text-sm">Public League</h3>
            </div>
            <Link
              to={`/league/${globalLeague.id}`}
              className="card block p-6 hover:shadow-lg transition-all"
              style={{ borderTop: "3px solid #3B82F6" }}
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="font-condensed text-xs tracking-wider px-3 py-1 rounded-full"
                      style={{ backgroundColor: "#EFF6FF", color: "#3B82F6" }}>
                      🌍 GLOBAL
                    </span>
                  </div>
                  <h3 className="font-display text-4xl tracking-wider mb-2" style={{ color: "#0A0B0F" }}>
                    {globalLeague.name.toUpperCase()}
                  </h3>
                  <p className="font-condensed text-gray-400 tracking-wide">
                    Every Predictorz member competes here — see how you rank against everyone
                  </p>
                </div>
                <div className="text-right ml-6">
                  <div className="font-display text-5xl" style={{ color: "#3B82F6" }}>{globalMemberCount}</div>
                  <div className="font-condensed text-gray-400 tracking-widest uppercase text-xs mt-1">Players</div>
                </div>
              </div>
              <div className="mt-5 pt-5 flex items-center justify-between"
                style={{ borderTop: "1px solid #E0E2DB" }}>
                <span className="font-condensed text-gray-400 tracking-wide text-sm">Open to all members</span>
                <span className="font-condensed tracking-wide text-sm" style={{ color: "#3B82F6" }}>VIEW LEADERBOARD →</span>
              </div>
            </Link>
          </div>
        )}

        {/* Private Leagues */}
        <div className="mb-6 fade-up fade-up-delay-1">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: "#C8FF00" }} />
            <h3 className="font-condensed text-gray-500 tracking-widest uppercase text-sm">My Private Leagues</h3>
          </div>
          {loading ? (
            <p className="font-condensed text-gray-400 text-xl mb-8">Loading leagues...</p>
          ) : leagues.length === 0 ? (
            <div className="card p-10 text-center mb-6">
              <p className="font-display text-3xl tracking-wider mb-2" style={{ color: "#0A0B0F" }}>NO LEAGUES YET</p>
              <p className="font-condensed text-gray-400 tracking-wide">Create or join one below</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {leagues.map((league) => (
                <Link key={league.id} to={`/league/${league.id}`}
                  className="card p-6 block transition-all hover:shadow-lg"
                  style={{ borderTop: "3px solid #C8FF00" }}>
                  <h3 className="font-display text-3xl tracking-wider mb-1" style={{ color: "#0A0B0F" }}>
                    {league.name.toUpperCase()}
                  </h3>
                  <p className="font-condensed text-gray-400 tracking-wide text-sm mb-4">
                    {league.members.length} member{league.members.length !== 1 ? "s" : ""}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="font-condensed tracking-widest text-sm px-3 py-1 rounded-lg"
                      style={{ backgroundColor: "#F5F6F2", color: "#666", fontFamily: "monospace" }}>
                      {league.inviteCode}
                    </span>
                    <span className="font-condensed text-gray-400 text-sm tracking-wide">View leaderboard →</span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Create & Join */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 fade-up fade-up-delay-2">

          <div className="card p-6">
            <h3 className="font-display text-2xl tracking-wider mb-1" style={{ color: "#0A0B0F" }}>CREATE A LEAGUE</h3>
            <p className="font-condensed text-gray-400 tracking-wide text-sm mb-6">
              Set up a private league and invite your friends
            </p>
            <form onSubmit={handleCreateLeague} className="space-y-3">
              <input
                type="text"
                placeholder="League name (e.g. The Lads)"
                value={newLeagueName}
                onChange={(e) => setNewLeagueName(e.target.value)}
                className="w-full rounded-xl px-4 py-3 text-lg"
              />
              <button
  type="submit"
  disabled={creating || leagues.filter((l) => l.createdBy === currentUser.uid).length >= MAX_LEAGUES_PER_USER}
  className="btn-lime w-full py-3 rounded-xl"
  style={{
    opacity: leagues.filter((l) => l.createdBy === currentUser.uid).length >= MAX_LEAGUES_PER_USER ? 0.4 : 1
  }}
>
  {creating ? "CREATING..." : "CREATE LEAGUE"}
</button>
            </form>
          </div>

          <div className="card p-6">
            <h3 className="font-display text-2xl tracking-wider mb-1" style={{ color: "#0A0B0F" }}>JOIN A LEAGUE</h3>
            <p className="font-condensed text-gray-400 tracking-wide text-sm mb-6">
              Enter an invite code from a friend
            </p>
            <form onSubmit={handleJoinLeague} className="space-y-3">
              <input
                type="text"
                placeholder="Enter code (e.g. XK92PL)"
                value={joinCode}
                onChange={(e) => setJoinCode(e.target.value)}
                className="w-full rounded-xl px-4 py-3 text-lg"
                style={{ fontFamily: "monospace", letterSpacing: "0.1em" }}
              />
              <button type="submit" className="btn-orange w-full py-3 rounded-xl">JOIN LEAGUE</button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}